const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"]);

/**
 * Nearest ancestor that scrolls vertically, or null when the window/document
 * is the vertical scroller.
 */
export const getNearestVerticalScrollElement = (
  element: HTMLElement | null,
): HTMLElement | null => {
  if (typeof window === "undefined" || element == null) {
    return null;
  }

  let parent = element.parentElement;
  while (parent != null && parent !== document.body && parent !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(parent);
    if (SCROLLABLE_OVERFLOW_VALUES.has(overflowY)) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

/** As above, but names the document's scroller rather than null, so callers that set `scrollTop`
 * always have an element. */
export const scrollParentOf = (element: HTMLElement | null): HTMLElement =>
  getNearestVerticalScrollElement(element) ??
  (document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement);

/** Visible band of a scrollport in client coordinates. Rows under `topOverlay` (sticky chrome over
 * the top edge) are excluded, since they are on screen but not visible. */
export const scrollportBounds = (
  element: HTMLElement,
  topOverlay?: HTMLElement | null,
): { top: number; bottom: number } => {
  const port =
    element === document.scrollingElement || element === document.documentElement
      ? { top: 0, bottom: window.innerHeight }
      : element.getBoundingClientRect();
  return {
    top: Math.max(port.top, topOverlay?.getBoundingClientRect().bottom ?? -Infinity),
    bottom: port.bottom,
  };
};
